// Modules
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ActivatedRoute } from '@angular/router';
import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Validators
import { MatchValues } from '@shared-module/validators/match.validator';

// Rxjs
import { Observable } from 'rxjs';

// Store - Reducer
import { AppState } from '@store/reducers';
// Store - Actions
import * as AuthActions from '@store/actions/auth.actions';
// Store - Selectors
import * as AuthSelectors from '@store/selectors/auth.selectors';

@Component({
    selector: 'sbi-cot-back-password-recovery',
    templateUrl: './password-recovery.component.html',
    styleUrls: ['./password-recovery.component.scss'],
    standalone: false
})
export class PasswordRecoveryComponent implements OnInit, OnDestroy {
  // Inject DestroyRef for subscription cleanup
  private _destroyRef = inject(DestroyRef);

  // Public variables
  public passwordRecoveryForm: FormGroup;
  public serverErrorMessage = '';

  // Public selectors
  public passwordChanging$: Observable<boolean> = this._store.pipe(
    select(AuthSelectors.selectPasswordChangingStatus)
  );
  public invalidPasswordRecoveryKey$: Observable<boolean> = this._store.pipe(
    select(AuthSelectors.selectPasswordRecoveryKeyStatus)
  );

  // Constructor
  constructor(
    private _route: ActivatedRoute,
    private _store: Store<AppState>,
    private _formBuilder: FormBuilder,
    private _actions$: Actions
  ) {}

  // On component init
  ngOnInit() {
    this._buildPasswordRecoveryForm();
    this._validatePasswordRecoveryKey();
    this._handleServerError();
  }

  // On component destroy
  ngOnDestroy() {
    this._store.dispatch(
      AuthActions.verifyPasswordResetResponse({ payload: { invalid: true } })
    );
  }

  // Private method to build the login form
  private _buildPasswordRecoveryForm(): void {
    this.passwordRecoveryForm = this._formBuilder.group({
      password: [
        '',
        Validators.compose([Validators.required, Validators.minLength(6)]),
      ],
      passwordRepeat: [
        '',
        Validators.compose([Validators.required, MatchValues('password')]),
      ],
    });
  }

  // Private method to validate the password recovery key
  private _validatePasswordRecoveryKey(): void {
    const key = { key: this._route.snapshot.paramMap.get('key') };
    this._store.dispatch(
      AuthActions.verifyPasswordResetRequested({ payload: key })
    );
  }

  // Listen for the backend validation message and show it under the field
  private _handleServerError(): void {
    this._actions$
      .pipe(
        ofType(AuthActions.passwordResetFailed),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(action => {
        const fieldError = action.payload?.fieldError;
        const control = this.passwordRecoveryForm.get('password');
        this.serverErrorMessage = fieldError || '';

        if (fieldError) {
          control.setErrors({ serverError: true });
          control.markAsTouched();
        } else if (control.hasError('serverError')) {
          control.setErrors(null);
        }
      });
  }

  // Public method to try an user login
  public onUserSubmit(): void {
    const key = this._route.snapshot.paramMap.get('key');
    const newPassword = this.passwordRecoveryForm.value.password;

    this._store.dispatch(
      AuthActions.passwordResetRequested({ payload: { key, newPassword } })
    );
  }

  // Public method to set the password and passord repeat values
  public setPasswordValue(event): void {
    this.passwordRecoveryForm.controls['password'].setValue(event.target.value);
    this.passwordRecoveryForm.controls['passwordRepeat'].setValue(
      this.passwordRecoveryForm.controls['passwordRepeat'].value
    );
  }

  // Public method to trigger the match password validator
  public setPasswordRepeatValue(event): void {
    this.passwordRecoveryForm.controls['passwordRepeat'].setValue(
      event.target.value
    );
  }
}
